import { Close } from '@mui/icons-material';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import { Alert, Button, FormControl, FormControlLabel, FormLabel, Grid, LinearProgress, Snackbar, Switch, TextField, Typography } from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Api from "../api/Api";
import ConfirmDialog from '../components/ConfirmDialog';
import PollTimer from '../components/PollTimer';
import ViewPollCard from '../components/ViewPollCard';
import { AppBarContext } from "../context/AppBarContextProvider";

const ManagePollPage = () => {
    const { id } = useParams();
    const { setTitle } = useContext(AppBarContext);
    const [poll, setPoll] = useState(undefined);
    const [votes, setVotes] = useState({ total: 0, counts: {} });
    const [creatingPost, setCreatingPost] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [alertState, setAlertState] = useState({ show: false, success: true, message: '' })

    useEffect(() => {
        const fetchPoll = async () => {
            let data = await Api.getPoll(id);
            if (data?._id) {
                setVotes(await Api.getVotes(id));
                setPoll(data);
                setTitle(data.name);
            }
        }
        fetchPoll();
        return () => setTitle(undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, setTitle])

    if (!poll) {
        return null;
    }

    const createPost = () => {
        setCreatingPost(true)
        const fetchPoll = async () => {
            const updatedPoll = await Api.getPoll(id);
            if (updatedPoll.tangledPostGUID) {
                setPoll(updatedPoll);
                setCreatingPost(false);
            } else {
                setTimeout(fetchPoll, 2000);
            }
        }
        setTimeout(fetchPoll, 2000);
    }

    const closePoll = async () => {
        setShowConfirmDialog(false)
        try {
            setPoll(await Api.closePoll(poll._id, poll.tangledIdemGUID));
        } catch (e) {
            setAlertState({ show: true, success: false, message: `Could not close the poll "${poll.question}".` })
        }
    }

    const handleCloseFormUpdateAlert = (/** @type {any} */ _event, /** @type {string} */ reason) => {
        if (reason === 'clickaway') {
            return
        }

        setAlertState({ show: false, success: true, message: '' })
    }

    return <Grid container spacing={2}>
        <Grid item lg={6}>
            {!poll.tangledPostGUID && poll.status === 1 && <Button startIcon={<VideoChatIcon />} variant="outlined" href={`https://tangled.com/content_edit.php?application_instance_url=${encodeURIComponent(`/a/E9xRK6Dw7HfU4zktPsD68794ZgmTuuz8#/view/${poll._id}`)}`} target="_blank"
                onClick={createPost}>
                Create Post
            </Button>}
        </Grid>
        {!poll.tangledPostGUID && poll.status === 1 && <Grid item lg={6}>
            <Alert variant='outlined' severity={'warning'}>
                You have to create a post to start getting votes on this poll.
            </Alert>
        </Grid>}
        {creatingPost && <Grid item xs={12}>
            <LinearProgress />
        </Grid>}
        <Grid item xs={12}>
            <Typography variant={'h6'} sx={{ color: 'white' }}>
                {poll.question}
            </Typography>
        </Grid>
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={!poll.blockTangledGroups} disabled />} label="Allow group to vote" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={!!poll.voteFee} disabled />} label="Pay to vote" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        {!!poll.voteFee && <Grid item xs={12}>
            <FormControl fullWidth>
                <FormLabel sx={{ color: 'white' }} >Millix amount</FormLabel>
                <TextField value={poll.voteFee} disabled />
            </FormControl>
        </Grid>}
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={!!poll.expireDate} disabled />} label="Automatically close" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        {!!poll.expireDate && <Grid item xs={12}>
            <FormControl fullWidth>
                <FormLabel sx={{ color: 'white' }} >Close time</FormLabel>
                <DateTimePicker defaultValue={dayjs(poll.expireDate)} disabled />
            </FormControl>
        </Grid>}
        <ViewPollCard poll={poll} initialVotes={votes} />
        <PollTimer poll={poll} onClosed={() => setPoll({ ...poll, status: 2 })}/>
        <Grid item xs={12} justifyContent={'center'} display={'flex'}>
            {poll.status === 1 && <Button startIcon={<Close />} variant={'contained'} onClick={() => setShowConfirmDialog(true)}>
                Close Poll
            </Button>}
            {poll.status === 2 && <Alert variant='outlined' severity={'info'}>
                This poll is closed.
            </Alert>}
        </Grid>
        {alertState.show && (
            <Snackbar
                open={alertState.show}
                autoHideDuration={4000}
                onClose={handleCloseFormUpdateAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseFormUpdateAlert}
                    severity={alertState.success ? 'success' : 'error'}
                    variant={'filled'}
                    elevation={6}
                    sx={{ width: '100%' }}
                >
                    {alertState.message}
                </Alert>
            </Snackbar>
        )}
        {showConfirmDialog && (
            <ConfirmDialog
                open={true}
                dialogTitle={'Close Poll'}
                contentText={`Do you want to close this poll?`}
                confirmText={'Close'}
                cancelText={'Cancel'}
                onCancel={() => setShowConfirmDialog(false)}
                confirmStartIcon={<Close />}
                onConfirm={closePoll}
            ></ConfirmDialog>
        )}
    </Grid>
}

export default ManagePollPage;